/**
 * AtlasVoiceModule — proximity voice chat (2026-07-21).
 *
 * The engine's WebRTC voice stack (perfect negotiation + Opus + per-peer HRTF
 * PannerNodes) does the hard parts; this module supplies the three Atlas
 * pieces its docs prescribe:
 *   1. SIGNALING over SpacetimeDB — the `voice_signal` mailbox table + the
 *      `sendVoiceSignal` reducer (auth = the game identity, zero extra infra).
 *   2. PROXIMITY peer selection — connect to live remotes within VOICE_RANGE_M,
 *      hysteresis so the boundary doesn't thrash, nearest-N cap (mesh voice
 *      scales to ~6 peers; beyond that needs an SFU — engine v2 roadmap).
 *   3. SPATIAL state — listener follows the local avatar+yaw; each peer's
 *      panner follows their interpolated remote position.
 *
 * Semantics: the mic button turns voice ON (talk + hear) or OFF (neither —
 * inbound signals are ignored while off, so the initiating side backs off).
 * Mic acquisition happens inside the click handler (iOS gesture rule) and the
 * AudioContext is resumed there too. Mute keeps you listening.
 *
 * Since 2026-09-20 remote voices play in the SHARED audio context, on the
 * `voice` bus: around the game gate (a call stays audible through the intro —
 * the player's choice, as before, now explicit in the graph) but UNDER the
 * master volume, the overload guard and the Voice-chat slider — it used to go
 * straight to a private destination, outside every control. The microphone and
 * the WebRTC transport are untouched. Peers share the one listener-relative
 * pose with everything else in the world (camera pitch and height included —
 * the old private listener was yaw-only at a fixed 1.6 m), so the NATIVE
 * listener is never moved here: moving it would mis-place every other panner.
 */
import type { App, IModule } from 'enginex';
import { blocks, muted } from '../lib/social_context.js';
import { VoiceChatModule } from 'enginex/voicechat/VoiceChatModule.js';
import type { IVoiceSignaling, VoiceSignalMessage } from 'enginex/voicechat/IVoiceSignaling.js';
import { atlas } from '../state.js';
import { bus } from '../bus.js';
import type { DbConnection } from '../module_bindings/index.js';
import { audioRuntime } from '../lib/audio/runtime.js';
import { DUCK_VOICECHAT } from '../lib/audio/mix_math.js';

const VOICE_RANGE_M = 40;      // hear/talk radius (half the text-chat radius)
const VOICE_DROP_M = 50;       // disconnect beyond (hysteresis band)
const MAX_PEERS = 6;           // WebRTC mesh comfort ceiling
const RETRY_BACKOFF_MS = 15_000;
/**
 * INTELLIGIBILITY. The engine default (inverse, refDistance 1) is 1/d: −20 dB at 10 m and −32 dB at the 40 m
 * connect range — a peer you are connected to but cannot hear. Full level inside 4 m, then 1/d: −8 dB at 10 m,
 * −20 dB at 40 m. A PROPOSAL (the research report's words: "needs listening tests") — one number, here.
 */
const VOICE_REF_DISTANCE_M = 4;
const EAR_HEIGHT_M = 1.6;

export class AtlasVoiceModule implements IModule {
  readonly name = 'AtlasVoice';
  private voice: VoiceChatModule | null = null;
  private ctx: AudioContext | null = null;
  private enabled = false;
  private muted = false;
  private acquiring = false; // accept inbound offers during mic acquisition
  private btn: HTMLButtonElement | null = null;
  private muteBtn: HTMLButtonElement | null = null;
  private tickTimer: number | null = null;
  private posTimer: number | null = null;
  private lastAttempt = new Map<string, number>(); // peerHex → ts of last connect try
  private signalHandler: ((from: string, msg: VoiceSignalMessage) => void) | null = null;

  init(_app: App): void {
    bus.on('atlas:account-ready', () => this.buildUi());
  }

  private buildUi(): void {
    if (this.btn) return;
    const host = document.getElementById('atlas-chat');
    const row = document.createElement('div');
    row.style.cssText = 'display:flex;gap:6px;margin-bottom:4px;pointer-events:auto;';
    const btn = document.createElement('button');
    btn.textContent = '🎤 voice: off';
    btn.style.cssText = 'padding:3px 10px;border:0;border-radius:6px;cursor:pointer;'
      + 'background:rgba(8,14,24,0.7);color:#9fb2c8;font:11px ui-monospace,Consolas,monospace;';
    btn.addEventListener('click', () => { void this.toggle(); });
    const muteBtn = document.createElement('button');
    muteBtn.textContent = '🔇 mute';
    muteBtn.style.cssText = btn.style.cssText + 'display:none;';
    muteBtn.addEventListener('click', () => this.toggleMute());
    row.appendChild(btn);
    row.appendChild(muteBtn);
    if (host) host.insertBefore(row, host.firstChild);
    else { row.style.cssText += 'position:fixed;left:12px;bottom:246px;z-index:40;'; document.body.appendChild(row); }
    this.btn = btn;
    this.muteBtn = muteBtn;
  }

  // ── Enable / disable (mic acquisition must live in the click) ─────────────
  private async toggle(): Promise<void> {
    if (this.enabled) { this.shutdown(); return; }
    const conn = atlas.conn;
    if (!conn || !atlas.identityHex) return;
    this.acquiring = true;
    try {
      // The shared context — created / resumed INSIDE this click (the iOS gesture rule still holds).
      const rt = audioRuntime();
      rt.unlock();
      if (!rt.ctx) throw new Error('no audio device');
      this.ctx = rt.ctx;
      await this.ctx.resume();
      this.voice ??= this.createVoice(conn, this.ctx);
      console.info('[AtlasVoice] toggle: acquireMic…');
      await this.voice.acquireMic();
      console.info('[AtlasVoice] toggle: mic OK');
      this.acquiring = false;
    } catch (e) {
      this.acquiring = false;
      if (this.btn) this.btn.textContent = '🎤 mic blocked';
      console.warn('[AtlasVoice] mic unavailable:', e);
      return;
    }
    this.enabled = true;
    this.muted = false;
    if (this.btn) { this.btn.textContent = '🎤 voice: ON'; this.btn.style.color = '#9fd48d'; }
    if (this.muteBtn) { this.muteBtn.style.display = ''; this.muteBtn.textContent = '🔇 mute'; }
    this.tickTimer = window.setInterval(() => this.maintainPeers(), 1000);
    this.posTimer = window.setInterval(() => this.updateSpatial(), 100);
  }

  private shutdown(): void {
    this.enabled = false;
    if (this.tickTimer !== null) { window.clearInterval(this.tickTimer); this.tickTimer = null; }
    if (this.posTimer !== null) { window.clearInterval(this.posTimer); this.posTimer = null; }
    if (this.voice) {
      for (const hex of [...this.lastAttempt.keys()]) this.voice.disconnectPeer(hex);
      this.voice.setMuted(true);
    }
    this.lastAttempt.clear();
    if (this.btn) { this.btn.textContent = '🎤 voice: off'; this.btn.style.color = '#9fb2c8'; }
    if (this.muteBtn) this.muteBtn.style.display = 'none';
  }

  private toggleMute(): void {
    if (!this.voice || !this.enabled) return;
    this.muted = !this.muted;
    this.voice.setMuted(this.muted);
    if (this.muteBtn) this.muteBtn.textContent = this.muted ? '🔊 unmute' : '🔇 mute';
  }

  // ── Engine voice + SpacetimeDB signaling ──────────────────────────────────
  private createVoice(conn: DbConnection, ctx: AudioContext): VoiceChatModule {
    const meHex = atlas.identityHex!;
    const signaling: IVoiceSignaling = {
      localPeerId: meHex,
      send: (peerId, msg) => {
        // Resolve hex → the Identity OBJECT from a replicated row (no SDK
        // hex-parsing dependency; player_state always has the sender's peers).
        for (const ps of conn.db.playerState.iter()) {
          if (ps.identity.toHexString() === peerId) {
            const kind = (msg as { kind?: string }).kind;
            if (kind !== 'candidate') console.info('[AtlasVoice] send', kind, '→', peerId.slice(0, 8));
            void conn.reducers.sendVoiceSignal({ target: ps.identity, payload: JSON.stringify(msg) })
              .catch((e: Error) => console.warn('[AtlasVoice] send failed', e.message));
            return;
          }
        }
      },
      onReceive: (handler) => { this.signalHandler = handler; },
    };
    // The mailbox only delivers if SUBSCRIBED — onInsert fires per cached row.
    // Tiny table (TTL-swept to ~dozens of rows), so a global sub is fine.
    conn.subscriptionBuilder().subscribe(['SELECT * FROM voice_signal']);
    conn.db.voiceSignal.onInsert((_c, row) => {
      if ((!this.enabled && !this.acquiring) || !this.signalHandler) return; // voice off = deaf to offers
      if (row.target.toHexString() !== meHex) return;
      const parsed = JSON.parse(row.payload) as VoiceSignalMessage & { kind?: string };
      if (parsed.kind !== 'candidate') console.info('[AtlasVoice] recv', parsed.kind, '←', row.sender.toHexString().slice(0, 8));
      try { this.signalHandler(row.sender.toHexString(), parsed); }
      catch { /* malformed signal — drop */ }
    });
    // parentBus = the `voice` bus: under the master and the Voice-chat volume, around the intro's game gate.
    const v = new VoiceChatModule(ctx, signaling, audioRuntime().busInput('voice'), {});
    v.router.config.refDistance = VOICE_REF_DISTANCE_M;
    v.on({
      // A peer talking asks the score and the beds to make a little room (a renewed lease — it lapses by itself).
      onSpeakingChanged: (_id: string, speaking: boolean) => { const rt = audioRuntime(); if (speaking && rt.ctx) rt.duck('voicechat', DUCK_VOICECHAT, rt.ctx.currentTime + 1.5); },
      onPeerJoined: (id: string) => {
      // The ANSWERING side enforces the boundary too: a blocked or muted peer that
      // initiated (lower hex) is dropped the moment its media path opens (2026-09-18).
      if (blocks().includes(id) || muted().has(id)) { try { v.disconnectPeer(id); } catch { /* gone */ } return; }
      console.info('[AtlasVoice] peer joined', id.slice(0, 10));
    } });
    return v;
  }

  // ── Proximity peer maintenance (1 Hz) ─────────────────────────────────────
  private maintainPeers(): void {
    const v = this.voice;
    const meHex = atlas.identityHex;
    if (!v || !meHex || !this.enabled) return;
    const now = Date.now();
    const inRange: Array<{ hex: string; d: number }> = [];
    const shut = new Set([...blocks(), ...muted()]); // a blocked or muted traveler is never a voice peer (2026-09-18)
    for (const [hex, r] of atlas.remotes) {
      const d = Math.hypot(r.targetX - atlas.localX, r.targetZ - atlas.localZ);
      if (shut.has(hex)) { if (this.lastAttempt.has(hex)) { v.disconnectPeer(hex); this.lastAttempt.delete(hex); } continue; }
      if (d <= VOICE_RANGE_M) inRange.push({ hex, d });
      else if (d > VOICE_DROP_M && this.lastAttempt.has(hex)) {
        v.disconnectPeer(hex);
        this.lastAttempt.delete(hex);
      }
    }
    inRange.sort((a, b) => a.d - b.d);
    for (const { hex } of inRange.slice(0, MAX_PEERS)) {
      if (this.lastAttempt.has(hex) && now - this.lastAttempt.get(hex)! < RETRY_BACKOFF_MS) continue;
      // One side initiates (lower hex) — the other answers via signaling. A
      // peer with voice OFF ignores the offer; backoff retries every 15 s.
      if (meHex < hex && !this.lastAttempt.has(hex)) {
        this.lastAttempt.set(hex, now);
        try { v.connectToPeer(hex); } catch { /* retried on backoff */ }
      } else if (this.lastAttempt.has(hex)) {
        this.lastAttempt.set(hex, now); // refresh, connection is live or answering
      }
    }
    // Remotes that vanished entirely (left world / went offline).
    for (const hex of [...this.lastAttempt.keys()]) {
      if (!atlas.remotes.has(hex)) { v.disconnectPeer(hex); this.lastAttempt.delete(hex); }
    }
  }

  // ── Spatial updates (10 Hz): peer panners, in LISTENER-LOCAL space ────────
  private readonly local: [number, number, number] = [0, 0, 0];
  private updateSpatial(): void {
    const v = this.voice;
    if (!v || !this.ctx || !this.enabled) return;
    const rt = audioRuntime();
    // The native listener stays at its default pose (the runtime's one spatial convention); each peer is expressed
    // relative to the shared listening pose — the subtraction in doubles, exact at any distance from the origin.
    for (const hex of this.lastAttempt.keys()) {
      const r = atlas.remotes.get(hex);
      if (!r) continue;
      // `targetY` is authoritative only while a remote FLIES or RIDES; on foot it is not the ground height, so a walker
      // is placed at the listener's own ear height (the old code put everyone on one flat plane for the same reason).
      const aloft = r.animState.startsWith('fly:') || r.animState.startsWith('ride:');
      const p = rt.toLocal(this.local, r.targetX, aloft ? r.targetY + EAR_HEIGHT_M : rt.listenerPose().y, r.targetZ);
      v.setPeerPosition(hex, p[0], p[1], p[2]);
    }
  }

  dispose(): void {
    this.shutdown();
    this.voice?.dispose();
    // (The context is the shared runtime's — a module never closes it.)
  }
}
